import type { Item, ItemStatus } from '../../domain/inventory';

/**
 * Active means the camera reports from its site. Once it's moved to another
 * site that's no longer known, so it waits as delivered until it reports
 * from the new site; the status reconciler then makes it Active again.
 * Moves that kept Active left cameras looking healthy at sites they had never
 * reported from (BIX -> BHM and the AAL moves, tidaltech/koi#8358).
 */
export const MOVED_ACTIVE_STATUS: ItemStatus = 'DELIVERED_TO_CUSTOMER';

export const MOVED_ACTIVE_HINT =
  'Moved from its site, so it stays Delivered to customer until it reports from the new one. It becomes Active again by itself.';

interface SiteChange {
  siteId?: string | undefined;
  locationSiteId?: string | undefined;
}

/** Whether the change puts the item at a site other than where it is now. */
export function movesToAnotherSite(
  item: Pick<Item, 'siteId' | 'locationSiteId'>,
  change: SiteChange,
): boolean {
  const target = change.locationSiteId || change.siteId;
  return Boolean(target) && target !== (item.locationSiteId ?? item.siteId);
}

/**
 * The status an Active item should get from a site change that doesn't set a
 * status itself; undefined when the status should stay as it is.
 */
export function statusForMove(
  item: Pick<Item, 'status' | 'siteId' | 'locationSiteId'>,
  change: SiteChange,
): ItemStatus | undefined {
  return item.status === 'ACTIVE' && movesToAnotherSite(item, change)
    ? MOVED_ACTIVE_STATUS
    : undefined;
}
